import { useEffect, useState } from 'react';

import { useLocation } from 'react-router-dom';

import { readSessionState, SessionState } from '../../../services/auth';
import clearStoredUserData from '../../common/clearStoredUserData';

/**
 * Whether a session can be read, or `null` until the first read answers.
 *
 * Re-read on each move between pages: the layout asking is reconciled rather
 * than remounted, so a read made only on mount would serve the whole visit. The
 * answer already held stands while a read is out.
 */
const useIsSignedIn = () => {
  const { pathname } = useLocation();
  const [isSignedIn, setIsSignedIn] = useState<boolean | null>(null);

  useEffect(() => {
    let mounted = true;

    readSessionState().then(state => {
      // The stores are shared, so they are cleared whether or not this reader
      // is here; a session Cognito refused is as gone as one signed out of
      if (state === SessionState.SignedOut) {
        clearStoredUserData();
      }

      if (!mounted) {
        return;
      }

      // Unconfirmed credentials leave a reader with what they already had:
      // turning one out would strand them where they cannot sign in again
      if (state === SessionState.Unreadable) {
        setIsSignedIn(current => current ?? false);
        return;
      }

      setIsSignedIn(state === SessionState.SignedIn);
    });

    return () => {
      mounted = false;
    };
  }, [pathname]);

  return isSignedIn;
};

export default useIsSignedIn;
